/**
 * PredictiveSearch Component
 * 
 * Handles predictive search functionality with:
 * - Debounced input
 * - Loading states
 * - Keyboard navigation
 * - Click outside to close
 * - Accessible ARIA attributes
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.searchReset = this.querySelector('[data-search-reset]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');
    this.loadingState = this.querySelector('#predictive-search-loading');
    this.errorState = this.querySelector('#predictive-search-error');
    this.resultsContainer = this.querySelector('#predictive-search-results');
    this.abortController = null;
    this.isLoading = false;
    this.cachedResults = new Map();

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Reset button
    this.searchReset.addEventListener('click', () => {
      this.input.value = '';
      this.onChange();
      this.input.focus();
    });

    // Input change with debounce
    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    // Keyboard navigation
    this.input.addEventListener('keydown', this.onKeydown.bind(this));

    // Close on escape
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
        this.input.blur();
      }
    });

    // Click outside to close
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target) && this.isOpen()) {
        this.close();
      }
    });

    // Prevent form submission on enter if results are open
    this.querySelector('form').addEventListener('submit', (event) => {
      if (this.isOpen() && this.getSelectedOption()) {
        event.preventDefault();
        this.selectCurrentOption();
      }
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    // Show/hide reset button
    this.searchReset.classList.toggle('hidden', !searchTerm.length);

    if (!searchTerm.length) {
      this.close();
      return;
    }

    // Update ARIA attributes
    this.input.setAttribute('aria-expanded', 'true');

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    // Check cache first
    if (this.cachedResults.has(searchTerm)) {
      this.renderResults(this.cachedResults.get(searchTerm));
      this.open();
      return;
    }

    // Cancel previous request
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.isLoading = true;
    this.showLoadingState();

    fetch(`/search/suggest?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product,collection,query&resources[limit_scope]=each&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku`, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        
        // Cache results
        this.cachedResults.set(searchTerm, resultsMarkup);
        
        this.renderResults(resultsMarkup);
        this.open();
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Search request aborted');
          return;
        }
        console.error('Search error:', error);
        this.showErrorState();
      })
      .finally(() => {
        this.isLoading = false;
        this.abortController = null;
      });
  }

  renderResults(markup) {
    // Hide loading and error states
    this.hideAllStates();
    
    // Extract just the results content from the returned markup
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = markup;
    const resultsContent = tempDiv.querySelector('#predictive-search-results');
    
    if (resultsContent) {
      this.predictiveSearchResults.innerHTML = resultsContent.outerHTML;
    }
  }

  showLoadingState() {
    this.hideAllStates();
    this.loadingState.style.display = 'block';
  }

  showErrorState() {
    this.hideAllStates();
    this.errorState.style.display = 'block';
  }

  hideAllStates() {
    this.loadingState.style.display = 'none';
    this.errorState.style.display = 'none';
    // Keep predictiveSearchResults as it may have results
  }

  open() {
    this.predictiveSearchResults.style.display = 'block';
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.hideAllStates();
    this.predictiveSearchResults.style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
    this.removeSelection();
  }

  isOpen() {
    return (
      this.predictiveSearchResults.style.display === 'block' ||
      this.loadingState.style.display === 'block' ||
      this.errorState.style.display === 'block'
    );
  }

  onKeydown(event) {
    if (!this.isOpen()) return;

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        if (this.getSelectedOption()) {
          event.preventDefault();
          this.selectCurrentOption();
        }
        break;
    }
  }

  switchOption(direction) {
    const options = this.getAllOptions();
    if (options.length === 0) return;

    const selectedOption = this.getSelectedOption();
    let selectedIndex = selectedOption ? Array.from(options).indexOf(selectedOption) : -1;

    // Remove current selection
    this.removeSelection();

    // Calculate new index
    if (direction === 'down') {
      selectedIndex = selectedIndex < options.length - 1 ? selectedIndex + 1 : 0;
    } else {
      selectedIndex = selectedIndex <= 0 ? options.length - 1 : selectedIndex - 1;
    }

    // Select new option
    const newOption = options[selectedIndex];
    if (newOption) {
      newOption.setAttribute('aria-selected', 'true');
      newOption.classList.add('bg-background-tertiary');
      newOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  selectCurrentOption() {
    const selectedOption = this.getSelectedOption();
    if (selectedOption) {
      const link = selectedOption.querySelector('a');
      if (link) {
        link.click();
      }
    }
  }

  getSelectedOption() {
    return this.predictiveSearchResults.querySelector('[aria-selected="true"]');
  }

  getAllOptions() {
    return this.predictiveSearchResults.querySelectorAll('[role="option"]');
  }

  removeSelection() {
    const selectedOption = this.getSelectedOption();
    if (selectedOption) {
      selectedOption.setAttribute('aria-selected', 'false');
      selectedOption.classList.remove('bg-background-tertiary');
    }
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);